import { z } from 'zod'
import { MoneySchema } from '../shared/money'
import { OptionalTextSchema } from '../shared/text'

/**
 * Schemas do produto da loja, fonte única dos tipos de produto (catálogo, carrinho e checkout importam daqui).
 *
 * A API devolve campos diferentes por tela (projeções em `Glofi.Data/Repositories/ProductCatalogRepository.cs`,
 * `FieldsByScreen`), então há um schema por formato:
 *
 * | Schema | Endpoints | Projeção no backend |
 * | --- | --- | --- |
 * | `ProductCardSchema` | `GET product/list-products`, `GET product/get-by-list-group/{id}`, recomendações | StoreCard |
 * | `ProductSearchResultSchema` | `POST product/search` (autocomplete do Navbar) | StoreSearch |
 * | `ProductSchema` | `GET product/get-by-slug/{slug}` (PDP), `GET product/get-for-cart/{id}` | documento inteiro |
 *
 * Todos levam o bloco de imagens (`images`, `imagesURL`, `thumbnailUrl`, `pictures`). `images` é a fonte nova
 * (objetos com url, miniatura, alt e ordem, já ordenados pela API); `imagesURL`, `thumbnailUrl` e `pictures` são os
 * campos antigos, calculados de `images` pela API e mantidos até a fase 4 da reestruturação das imagens no backend.
 *
 * Tolerância: textos que o DTO declara anuláveis viram `''` e listas nulas viram `[]`, para a tela não tratar `null`.
 * Estritos: `id` e `slug` (o card é link para a PDP) e os preços (o front calcula e exibe).
 */

// ===== PRIMITIVOS =====

const text = OptionalTextSchema

/** Lista que o backend pode mandar nula ou omitir: vira `[]`. */
const list = <T extends z.ZodType>(item: T) =>
    z
        .array(item)
        .nullish()
        .transform((value) => value ?? [])

// ===== IMAGENS =====

/** Item de `images` (biblioteca central de imagens). Cópias antigas guardam só o `id`, por isso url e alt toleram nulo. */
export const ProductImageSchema = z.object({
    id: z.string(),
    url: text,
    thumbnailUrl: text,
    alt: text,
    order: z.number(),
})

/** Item de `pictures` (formato antigo, derivado de `images` pela API). */
export const ProductPictureSchema = z.object({
    name: text,
    description: text,
    order: z.number(),
    url: text,
})

/** Bloco de imagens comum a todos os formatos de produto. */
const productImageFields = {
    /** URL da imagem de capa (a imagem cheia da primeira de `images`, não a miniatura). */
    thumbnailUrl: text,
    imagesURL: list(z.string()),
    images: list(ProductImageSchema),
    pictures: list(ProductPictureSchema),
}

// ===== CARD (listagem, vitrine, recomendações) =====

/**
 * Card de produto. `looseObject` porque as recomendações (`cart/product-*-recomendation`) devolvem o produto inteiro:
 * os campos a mais passam sem quebrar o parse.
 */
export const ProductCardSchema = z.looseObject({
    id: z.string(),
    name: text,
    slug: z.string(),
    /** Preço de tabela. */
    price: MoneySchema,
    /** Preço com desconto (o que a loja cobra); igual ao `price` quando não há desconto. */
    discountedPrice: MoneySchema,
    stockQuantity: z.number(),
    shortDescription: text,
    /** Chave de cor da página do produto (ex.: `nitrilo-blue`), mapeada para cor no front. */
    mainPageColor: text,
    ...productImageFields,
})

// ===== RESULTADO DA BUSCA (Navbar) =====

/** Resultado do autocomplete: sem preço nem estoque. */
export const ProductSearchResultSchema = z.object({
    id: z.string(),
    name: text,
    slug: z.string(),
    shortDescription: text,
    ...productImageFields,
})

// ===== PRODUTO COMPLETO (PDP) =====

/** Produto completo da PDP (`get-by-slug`) e do `get-for-cart` (este sem `active`). */
export const ProductSchema = ProductCardSchema.extend({
    sku: text,
    categories: list(z.string()),
    tags: list(z.string()),
    /** Grupo de variações (mesmo produto em outros tamanhos); os tamanhos vêm em `productSizes` da resposta. */
    group: text,
    order: z.number(),
    /** Tamanho deste produto (EP, P, M, G, EG...). */
    productSize: text,
    type: text,
    visible: z.boolean(),
    /** Ausente no `get-for-cart`. */
    active: z.boolean().optional(),
    /** HTML. */
    fullDescription: text,
    /** HTML da aba de especificações (a PDP legada lê `specifications`, que a API não manda). */
    specification: text,
    specificationCss: text,
    sellerName: text,
    seoTitle: text,
    seoDescription: text,
    /** Texto de prazo de envio; quase sempre nulo. */
    shippingText: z.string().nullish(),
})

/** Outro tamanho do mesmo grupo, para o seletor de tamanho da PDP. */
export const ProductSizeOptionSchema = z.object({
    size: text,
    slug: z.string(),
})

// ===== TIPOS =====

export type IProductImage = z.infer<typeof ProductImageSchema>
export type IProductPicture = z.infer<typeof ProductPictureSchema>
export type IProductCard = z.infer<typeof ProductCardSchema>
export type IProductSearchResult = z.infer<typeof ProductSearchResultSchema>
export type IProduct = z.infer<typeof ProductSchema>
export type IProductSizeOption = z.infer<typeof ProductSizeOptionSchema>

/** Produto da PDP com os tamanhos do grupo (resultado do `getProductBySlugService`). */
export interface IProductDetail {
    product: IProduct
    /** Tamanhos do mesmo grupo, para o seletor de tamanho, em ordem de tamanho (EP, P, M, G, EG). */
    productSizes: IProductSizeOption[]
}
